/**
 * SkeletonScreen — Shimmer loading states for Dashboard, Portfolio, Verdict, Insights.
 *
 * Shows instantly on navigation before data loads. Matches layout dimensions.
 */

import { T } from '@/styles/tokens';

const SHIMMER_STYLE = `
@keyframes skeletonShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
`;

interface SkeletonBlockProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: number;
  style?: React.CSSProperties;
}

export function SkeletonBlock({ width = '100%', height = 16, borderRadius = 8, style }: SkeletonBlockProps) {
  return (
    <>
      <style>{SHIMMER_STYLE}</style>
      <div style={{
        width,
        height,
        borderRadius,
        backgroundImage: `linear-gradient(90deg, ${T.border} 0%, ${T.borderLight} 50%, ${T.border} 100%)`,
        backgroundSize: '200% 100%',
        animation: 'skeletonShimmer 1.5s infinite linear',
        ...style,
      }} />
    </>
  );
}

export function DashboardSkeleton() {
  return (
    <div style={{ fontFamily: T.fontFamily }}>
      {/* Teal gradient zone (static) */}
      <div style={{
        background: 'linear-gradient(160deg, #2A9089 0%, #3DABA5 60%, #4dc4bd 100%)',
        height: 280,
      }} />
      {/* Content with overlap */}
      <div style={{
        marginTop: -24,
        padding: '0 16px 40px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        position: 'relative',
        zIndex: 1,
      }}>
        <SkeletonBlock height={80} borderRadius={16} style={{ background: T.white, boxShadow: T.shadowRaised }} />
        <SkeletonBlock height={100} borderRadius={12} style={{ background: T.white, boxShadow: T.shadowRaised }} />
        <SkeletonBlock height={72} borderRadius={12} style={{ background: T.white, boxShadow: T.shadowRaised }} />
      </div>
    </div>
  );
}

export function PortfolioSkeleton() {
  return (
    <div style={{ padding: '20px 20px 40px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 0',
        }}>
          <SkeletonBlock width={40} height={40} borderRadius={12} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <SkeletonBlock width="60%" height={14} />
            <SkeletonBlock width="40%" height={12} />
          </div>
          <SkeletonBlock width={60} height={16} />
        </div>
      ))}
    </div>
  );
}

export function VerdictDetailSkeleton() {
  return (
    <div style={{ padding: '32px 20px 40px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <SkeletonBlock width={120} height={16} />
        <SkeletonBlock width={160} height={42} />
        <SkeletonBlock width={100} height={12} />
      </div>
      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SkeletonBlock height={100} borderRadius={16} style={{ background: T.white }} />
        <SkeletonBlock height={140} borderRadius={16} style={{ background: T.white }} />
      </div>
    </div>
  );
}

export function InsightsSkeleton() {
  return (
    <div style={{ fontFamily: T.fontFamily }}>
      <div style={{
        background: 'linear-gradient(160deg, #2A9089 0%, #3DABA5 60%, #4dc4bd 100%)',
        height: 120,
      }} />
      <div style={{
        marginTop: -20,
        padding: '0 16px 40px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <SkeletonBlock width={80} height={28} borderRadius={14} />
          <SkeletonBlock width={80} height={28} borderRadius={14} />
          <SkeletonBlock width={80} height={28} borderRadius={14} />
        </div>
        <SkeletonBlock height={100} borderRadius={12} style={{ background: T.white, boxShadow: T.shadowRaised }} />
        <SkeletonBlock height={180} borderRadius={12} style={{ background: T.white, boxShadow: T.shadowRaised }} />
      </div>
    </div>
  );
}
